import React, { useState, useEffect } from 'react'
import {
  FolderIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { materialCategoriesApi } from '../../services/adminApi'

interface Category {
  id: number
  name: string
  name_en?: string
  parent_id: number | null
  sort_order: number
  icon?: string
  description?: string
  is_active: number
  material_count?: number
  children?: Category[]
}

interface CategoryFormData {
  name: string
  name_en: string
  parent_id: number | null
  icon: string
  description: string
}

export const CategoryManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([])
  const [flatCategories, setFlatCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
  
  // 模态框状态
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null)
  
  // 表单数据
  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    name_en: '',
    parent_id: null,
    icon: '',
    description: ''
  })

  // 加载分类列表
  const loadCategories = async () => {
    try {
      setLoading(true)
      const [treeResponse, flatResponse] = await Promise.all([
        materialCategoriesApi.getCategories(false),
        materialCategoriesApi.getCategories(true)
      ])
      
      if (treeResponse.success) {
        setCategories(treeResponse.data || [])
      }
      if (flatResponse.success) {
        setFlatCategories(flatResponse.data || [])
      }
    } catch (error) {
      console.error('加载分类失败:', error)
      toast.error('加载分类失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCategories()
  }, [])

  // 切换展开/收起
  const toggleExpand = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  // 重置表单
  const resetForm = () => {
    setFormData({
      name: '',
      name_en: '',
      parent_id: null,
      icon: '',
      description: ''
    })
  }

  // 打开创建模态框
  const openCreateModal = (parentId?: number) => {
    resetForm()
    if (parentId) {
      setFormData(prev => ({ ...prev, parent_id: parentId }))
    }
    setShowCreateModal(true)
  }

  // 打开编辑模态框
  const openEditModal = (category: Category) => {
    setSelectedCategory(category)
    setFormData({
      name: category.name,
      name_en: category.name_en || '',
      parent_id: category.parent_id,
      icon: category.icon || '',
      description: category.description || ''
    })
    setShowEditModal(true)
  }

  // 创建分类
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('请输入分类名称')
      return
    }

    try {
      const response = await materialCategoriesApi.createCategory({
        name: formData.name.trim(),
        name_en: formData.name_en.trim() || undefined,
        parent_id: formData.parent_id ?? undefined,
        icon: formData.icon.trim() || undefined,
        description: formData.description.trim() || undefined
      })

      if (response.success) {
        toast.success('分类创建成功')
        setShowCreateModal(false)
        resetForm()
        loadCategories()
      } else {
        toast.error(response.error?.message || '创建失败')
      }
    } catch (error: any) {
      toast.error(error.message || '创建失败')
    }
  }

  // 更新分类
  const handleUpdate = async () => {
    if (!selectedCategory) return
    if (!formData.name.trim()) {
      toast.error('请输入分类名称')
      return
    }

    try {
      const response = await materialCategoriesApi.updateCategory(selectedCategory.id, {
        name: formData.name.trim(),
        name_en: formData.name_en.trim() || undefined,
        parent_id: formData.parent_id ?? undefined,
        icon: formData.icon.trim() || undefined,
        description: formData.description.trim() || undefined
      })

      if (response.success) {
        toast.success('分类更新成功')
        setShowEditModal(false)
        setSelectedCategory(null)
        resetForm()
        loadCategories()
      } else {
        toast.error(response.error?.message || '更新失败')
      }
    } catch (error: any) {
      toast.error(error.message || '更新失败')
    }
  }

  // 删除分类
  const handleDelete = async (force: boolean = false) => {
    if (!selectedCategory) return

    try {
      const response = await materialCategoriesApi.deleteCategory(selectedCategory.id, force)

      if (response.success) {
        toast.success('分类删除成功')
        setShowDeleteConfirm(false)
        setSelectedCategory(null)
        loadCategories()
      } else {
        toast.error(response.error?.message || '删除失败')
      }
    } catch (error: any) {
      toast.error(error.message || '删除失败')
    }
  }

  // 获取父分类名称
  const getParentName = (parentId: number | null): string => {
    if (!parentId) return '顶级分类'
    const parent = flatCategories.find(c => c.id === parentId)
    return parent ? parent.name : '未知'
  }

  // 渲染分类树
  const renderCategoryTree = (cats: Category[], level: number = 0) => {
    return cats.map(cat => {
      const hasChildren = cat.children && cat.children.length > 0
      const isExpanded = expandedIds.has(cat.id)

      return (
        <div key={cat.id}>
          <div 
            className={`flex items-center px-4 py-3 hover:bg-gray-50 border-b border-gray-100 ${
              level > 0 ? 'bg-gray-50/50' : ''
            }`}
            style={{ paddingLeft: `${16 + level * 24}px` }}
          >
            {/* 展开/收起按钮 */}
            <div className="w-6 mr-2">
              {hasChildren && (
                <button
                  onClick={() => toggleExpand(cat.id)}
                  className="p-0.5 hover:bg-gray-200 rounded"
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 text-gray-500" />
                  )}
                </button>
              )}
            </div>

            {/* 图标 */}
            <FolderIcon className="w-5 h-5 text-purple-500 mr-3" />

            {/* ID */}
            <div className="w-16 mr-3 text-xs font-mono text-gray-400">
              {cat.id}
            </div>

            {/* 名称 */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <span className="font-medium text-gray-900">{cat.name}</span>
                {cat.name_en && (
                  <span className="ml-2 text-sm text-gray-500">({cat.name_en})</span>
                )}
              </div>
              {cat.description && (
                <p className="text-xs text-gray-400 truncate mt-0.5">{cat.description}</p>
              )}
            </div>

            {/* 教材数量 */}
            <div className="px-3">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {cat.material_count || 0} 个教材
              </span>
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => openCreateModal(cat.id)}
                className="p-1.5 text-green-600 hover:bg-green-100 rounded"
                title="添加子分类"
              >
                <PlusIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => openEditModal(cat)}
                className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                title="编辑"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setSelectedCategory(cat)
                  setShowDeleteConfirm(true)
                }}
                className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                title="删除"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* 子分类 */}
          {hasChildren && isExpanded && (
            <div>{renderCategoryTree(cat.children!, level + 1)}</div>
          )}
        </div>
      )
    })
  }

  // 渲染父分类选择器
  const renderParentOptions = (cats: Category[], level: number = 0, excludeId?: number): React.ReactNode => {
    return cats
      .filter(cat => cat.id !== excludeId)
      .map(cat => (
        <React.Fragment key={cat.id}>
          <option value={cat.id}>
            {'　'.repeat(level)}{cat.name}
          </option>
          {cat.children && cat.children.length > 0 && 
            renderParentOptions(cat.children, level + 1, excludeId)}
        </React.Fragment>
      ))
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">分类管理</h1>
          <p className="mt-1 text-sm text-gray-500">管理教材分类目录结构</p>
        </div>
        <button
          onClick={() => openCreateModal()}
          className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          添加分类
        </button>
      </div>

      {/* 分类树 */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-12">
            <FolderIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">暂无分类数据</p>
            <button
              onClick={() => openCreateModal()}
              className="mt-4 text-purple-600 hover:text-purple-700"
            >
              创建第一个分类
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {/* 表头 */}
            <div className="flex items-center px-4 py-3 bg-gray-50 text-sm font-medium text-gray-500">
              <div className="w-6 mr-2"></div>
              <div className="w-5 mr-3"></div>
              <div className="w-16 mr-3">ID</div>
              <div className="flex-1">分类名称</div>
              <div className="px-3 w-24 text-center">教材数</div>
              <div className="w-28 text-center">操作</div>
            </div>
            {/* 分类列表 */}
            {renderCategoryTree(categories)}
          </div>
        )}
      </div>

      {/* 创建/编辑模态框 */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {showCreateModal ? '添加分类' : '编辑分类'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedCategory(null)
                  resetForm()
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  分类名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="请输入中文名称"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  英文名称
                </label>
                <input
                  type="text"
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="English name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  父分类
                </label>
                <select
                  value={formData.parent_id ?? ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    parent_id: e.target.value ? parseInt(e.target.value) : null 
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">顶级分类</option>
                  {renderParentOptions(categories, 0, selectedCategory?.id)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  描述
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="分类描述（可选）"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedCategory(null)
                  resetForm()
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={showCreateModal ? handleCreate : handleUpdate}
                className="px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700"
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 删除确认 */}
      {showDeleteConfirm && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">确认删除</h3>
            
            {(selectedCategory.material_count && selectedCategory.material_count > 0) || 
             (selectedCategory.children && selectedCategory.children.length > 0) ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-yellow-800 font-medium">警告</p>
                <p className="text-yellow-700 text-sm mt-1">
                  {selectedCategory.material_count && selectedCategory.material_count > 0 && (
                    <>该分类下有 <span className="font-bold">{selectedCategory.material_count}</span> 个教材。</>
                  )}
                  {selectedCategory.children && selectedCategory.children.length > 0 && (
                    <>该分类包含 <span className="font-bold">{selectedCategory.children.length}</span> 个子分类。</>
                  )}
                  删除后，相关教材将变为未分类状态。
                </p>
              </div>
            ) : null}

            <p className="text-gray-600 mb-6">
              确定要删除分类 "<span className="font-medium">{selectedCategory.name}</span>" 吗？
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setSelectedCategory(null)
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(true)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CategoryManagement
